import { skipToken } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { useState } from 'react';

import { BookingLinkForm } from '@/modules/hiring-manager/components/BookingLinkForm';
import {
  IntegrationRow,
  IntegrationStatus,
} from '@/modules/hiring-manager/components/IntegrationRow';
import { isJobVisibleToHiringManagers } from '@/modules/jobs/jobStates';
import { useJobsQuery } from '@/modules/jobs/queries/useJobsQuery';
import { Button } from '@/modules/ui/components/button/Button';
import type { CurrentUserApi } from '@/modules/users/types';

interface BookingLinkRowProps {
  user: CurrentUserApi;
  tenantId: string;
}

/** The signed-in HM's booking link, sent to candidates they pre-screen themselves. */
export function BookingLinkRow({ user, tenantId }: BookingLinkRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const meetingUrl = user.meetingUrl;
  const showForm = isEditing || !meetingUrl;
  // Same query as the Jobs page, so it is usually cached. The note is a hint
  // on top of the row, so a failed load just leaves it out.
  const jobsQuery = useJobsQuery(
    meetingUrl ? skipToken : { companyId: tenantId },
  );
  // Only the owner's link goes to candidates (see HMJobAutomation).
  const waitingTitles = (jobsQuery.data ?? [])
    .filter(
      (job) =>
        job.ownerId === user.id &&
        job.screeningMethod === 'HiringManager' &&
        isJobVisibleToHiringManagers(job.state),
    )
    .map((job) => job.title);

  return (
    <IntegrationRow
      action={
        !showForm && (
          <Button
            className="h-8 rounded-md"
            onClick={() => setIsEditing(true)}
            size="sm"
            variant="outline"
          >
            Change link
          </Button>
        )
      }
      description="When you pre-screen candidates yourself, they get this link to book a call with you. Any scheduling page works, such as Calendly."
      icon={CalendarClock}
      name="Booking link"
      note={
        !meetingUrl &&
        waitingTitles.length > 0 &&
        `Needed for ${waitingTitles.length} ${waitingTitles.length === 1 ? 'job' : 'jobs'} where you pre-screen yourself: ${waitingTitles.join(', ')}`
      }
      status={
        <IntegrationStatus isActive={Boolean(meetingUrl)}>
          {meetingUrl ? 'Added' : 'Not added'}
        </IntegrationStatus>
      }
      usedBy="Pre-screening"
    >
      {showForm ? (
        <BookingLinkForm
          initialValue={meetingUrl}
          onCancel={meetingUrl ? () => setIsEditing(false) : undefined}
          onSaved={() => setIsEditing(false)}
          submitLabel="Save link"
        />
      ) : (
        <a
          className="text-primary-700 text-[13px] break-all underline underline-offset-2"
          href={meetingUrl}
          rel="noreferrer"
          target="_blank"
        >
          {meetingUrl}
        </a>
      )}
    </IntegrationRow>
  );
}
